import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowRight, Loader2, Check, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { fetchMachineArticles, createAssociations, MachineArticle } from '../api';
import { CatEscluseBadge } from './CatEscluseBadge';

interface Props {
  baseId: string;
  baseModel: string;
  baseBrand: string;
  rebrandId: string;
  rebrandModel: string;
  rebrandBrand: string;
  /** Categorie articolo escluse dalla relazione di rebrand — protette da spostamento finché bloccate. */
  categorieEscluse: string[];
  onClose: () => void;
  /** Invocato dopo ogni sincronizzazione riuscita, per aggiornare i conteggi in tabella. */
  onSynced?: () => void;
}

type PendingKind = 'fromBase' | 'fromRebrand';
interface PendingItem { article: MachineArticle; kind: PendingKind }

/**
 * Modal di confronto/sincronizzazione articoli tra una macchina produttore
 * e un suo modello rebrand: mostra solo produttore / condivisi / solo
 * rebrand. Cliccando un articolo "solo X" lo si sposta visivamente nella
 * colonna centrale "Condivisi" come anteprima "in attesa" (colore diverso +
 * etichetta di provenienza); un secondo click lo riporta alla colonna
 * originale. Solo alla conferma le modifiche pendenti vengono persistite
 * (al massimo 2 chiamate batch, una per direzione).
 */
export default function CompatSyncModal({
  baseId, baseModel, baseBrand, rebrandId, rebrandModel, rebrandBrand, categorieEscluse, onClose, onSynced,
}: Props) {
  const [baseArticles, setBaseArticles] = useState<MachineArticle[] | null>(null);
  const [rebrandArticles, setRebrandArticles] = useState<MachineArticle[] | null>(null);
  const [confirming, setConfirming] = useState(false);
  // Se sbloccato, anche gli articoli delle categorie escluse possono essere spostati/selezionati.
  const [unlocked, setUnlocked] = useState(false);

  // SKU spostati (in anteprima) da "Solo produttore" verso "Condivisi" (destinati al rebrand),
  // e SKU spostati da "Solo rebrand" verso "Condivisi" (destinati alla base).
  const [pendingToRebrand, setPendingToRebrand] = useState<Set<string>>(new Set());
  const [pendingToBase, setPendingToBase] = useState<Set<string>>(new Set());

  const load = () => {
    setBaseArticles(null); setRebrandArticles(null);
    Promise.all([fetchMachineArticles(baseId), fetchMachineArticles(rebrandId)])
      .then(([b, r]) => { setBaseArticles(b); setRebrandArticles(r); })
      .catch(() => toast.error('Errore nel caricamento degli articoli'));
  };

  useEffect(() => { load(); }, [baseId, rebrandId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Diff "grezzo" (prima di rimuovere gli articoli in stato "in attesa")
  const { shared, rawOnlyBase, rawOnlyRebrand } = useMemo(() => {
    if (!baseArticles || !rebrandArticles) {
      return { shared: [] as MachineArticle[], rawOnlyBase: [] as MachineArticle[], rawOnlyRebrand: [] as MachineArticle[] };
    }
    const baseMap = new Map(baseArticles.map(a => [a.sku, a]));
    const rebrandMap = new Map(rebrandArticles.map(a => [a.sku, a]));
    const sharedArr: MachineArticle[] = [];
    const onlyBaseArr: MachineArticle[] = [];
    const onlyRebrandArr: MachineArticle[] = [];
    baseMap.forEach((a, sku) => { if (rebrandMap.has(sku)) sharedArr.push(a); else onlyBaseArr.push(a); });
    rebrandMap.forEach((a, sku) => { if (!baseMap.has(sku)) onlyRebrandArr.push(a); });
    return { shared: sharedArr, rawOnlyBase: onlyBaseArr, rawOnlyRebrand: onlyRebrandArr };
  }, [baseArticles, rebrandArticles]);

  // Le selezioni pendenti non hanno più senso dopo un reload (gli SKU potrebbero
  // essere già stati sincronizzati) — vengono ripulite ad ogni load() riuscito.
  useEffect(() => {
    setPendingToRebrand(prev => new Set(Array.from(prev).filter(sku => rawOnlyBase.some(a => a.sku === sku))));
    setPendingToBase(prev => new Set(Array.from(prev).filter(sku => rawOnlyRebrand.some(a => a.sku === sku))));
  }, [rawOnlyBase, rawOnlyRebrand]);

  // Colonne laterali visualizzate: raw meno gli articoli spostati in anteprima al centro
  const displayedOnlyBase = useMemo(() => rawOnlyBase.filter(a => !pendingToRebrand.has(a.sku)), [rawOnlyBase, pendingToRebrand]);
  const displayedOnlyRebrand = useMemo(() => rawOnlyRebrand.filter(a => !pendingToBase.has(a.sku)), [rawOnlyRebrand, pendingToBase]);

  // Articoli di categoria esclusa: protetti da spostamento/selezione finché bloccati.
  const isExcludedCat = (a: MachineArticle) => categorieEscluse.includes(a.cat);
  const selectableOnlyBase = useMemo(
    () => unlocked ? rawOnlyBase : rawOnlyBase.filter(a => !isExcludedCat(a)),
    [rawOnlyBase, unlocked, categorieEscluse],
  );
  const selectableOnlyRebrand = useMemo(
    () => unlocked ? rawOnlyRebrand : rawOnlyRebrand.filter(a => !isExcludedCat(a)),
    [rawOnlyRebrand, unlocked, categorieEscluse],
  );

  // Articoli "in attesa" mostrati al centro, con provenienza
  const pendingItems: PendingItem[] = useMemo(() => [
    ...rawOnlyBase.filter(a => pendingToRebrand.has(a.sku)).map(article => ({ article, kind: 'fromBase' as PendingKind })),
    ...rawOnlyRebrand.filter(a => pendingToBase.has(a.sku)).map(article => ({ article, kind: 'fromRebrand' as PendingKind })),
  ], [rawOnlyBase, rawOnlyRebrand, pendingToRebrand, pendingToBase]);

  // Toggle: sposta un articolo verso il centro (se era "solo X") o lo riporta
  // alla colonna originale (se era già "in attesa") — stessa funzione per entrambe le direzioni.
  // L'annullamento (rimozione) è sempre consentito; l'aggiunta è bloccata per le
  // categorie escluse finché il lucchetto non viene sbloccato.
  const toggleToRebrand = (sku: string) => setPendingToRebrand(prev => {
    const next = new Set(prev);
    if (next.has(sku)) {
      next.delete(sku);
    } else {
      const article = rawOnlyBase.find(a => a.sku === sku);
      if (!unlocked && article && isExcludedCat(article)) return prev;
      next.add(sku);
    }
    return next;
  });
  const toggleToBase = (sku: string) => setPendingToBase(prev => {
    const next = new Set(prev);
    if (next.has(sku)) {
      next.delete(sku);
    } else {
      const article = rawOnlyRebrand.find(a => a.sku === sku);
      if (!unlocked && article && isExcludedCat(article)) return prev;
      next.add(sku);
    }
    return next;
  });
  const toggleAllToRebrand = () => setPendingToRebrand(prev =>
    prev.size === selectableOnlyBase.length ? new Set() : new Set(selectableOnlyBase.map(a => a.sku)));
  const toggleAllToBase = () => setPendingToBase(prev =>
    prev.size === selectableOnlyRebrand.length ? new Set() : new Set(selectableOnlyRebrand.map(a => a.sku)));

  const totalPending = pendingToRebrand.size + pendingToBase.size;

  const handleConfirm = async () => {
    if (totalPending === 0) return;
    setConfirming(true);
    try {
      const calls: Promise<{ created: number }>[] = [];
      if (pendingToRebrand.size > 0) calls.push(createAssociations({ skus: Array.from(pendingToRebrand), machineIds: [rebrandId] }));
      if (pendingToBase.size > 0) calls.push(createAssociations({ skus: Array.from(pendingToBase), machineIds: [baseId] }));
      const results = await Promise.all(calls);
      const totalCreated = results.reduce((sum, r) => sum + r.created, 0);
      toast.success(`${totalCreated} associazioni sincronizzate`);
      setPendingToRebrand(new Set()); setPendingToBase(new Set());
      load();
      onSynced?.();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally {
      setConfirming(false);
    }
  };

  const loading = baseArticles === null || rebrandArticles === null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="compat-sync-modal">
      <div className="bg-white rounded-[12px] shadow-2xl max-w-5xl w-full h-[80vh] flex flex-col">
        <div className="flex-none px-6 py-4 border-b border-[#eee] flex items-center justify-between">
          <div>
            <div className="text-[16px] font-bold text-[#1f1f1d] flex items-center gap-1.5">
              {baseBrand} {baseModel} <ArrowRight className="w-4 h-4 text-[#9b9b96]" /> {rebrandBrand} {rebrandModel}
            </div>
            <div className="text-[12px] text-[#5c5c57] mt-0.5">Confronto associazioni e sincronizzazione — clicca un articolo per spostarlo in Condivisi</div>
            {categorieEscluse.length > 0 && (
              <div className="flex items-center gap-2 mt-2">
                <CatEscluseBadge cats={categorieEscluse} testId="compat-sync-catE-header" showLabel />
                <button
                  onClick={() => setUnlocked(u => !u)}
                  data-testid="compat-sync-toggle-lock"
                  title={unlocked
                    ? 'Blocca di nuovo le categorie escluse'
                    : 'Sblocca: consenti di spostare anche gli articoli delle categorie escluse'}
                  className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border transition-colors ${
                    unlocked
                      ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                      : 'bg-[#f1f1ee] border-[#d8d8d4] text-[#5c5c57] hover:bg-[#e8e8e4]'
                  }`}
                >
                  {unlocked ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                  {unlocked ? 'Sbloccato' : 'Bloccate'}
                </button>
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="compat-sync-close" className="text-[#9b9b96] hover:text-[#5c5c57]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-[#5c5c57] gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento…
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-3 divide-x divide-[#e4e4e1]">
            <SideColumn
              title={`Solo ${baseBrand}`}
              items={displayedOnlyBase}
              selectableCount={selectableOnlyBase.length}
              color="#5c5c57"
              testIdPrefix="compat-sync-onlybase"
              onItemClick={a => toggleToRebrand(a.sku)}
              onToggleAll={toggleAllToRebrand}
              allSelected={pendingToRebrand.size === selectableOnlyBase.length && selectableOnlyBase.length > 0}
              categorieEscluse={categorieEscluse}
              unlocked={unlocked}
            />
            <CenterColumn
              shared={shared}
              pendingItems={pendingItems}
              baseBrand={baseBrand}
              rebrandBrand={rebrandBrand}
              onUndoFromBase={sku => toggleToRebrand(sku)}
              onUndoFromRebrand={sku => toggleToBase(sku)}
            />
            <SideColumn
              title={`Solo ${rebrandBrand}`}
              items={displayedOnlyRebrand}
              selectableCount={selectableOnlyRebrand.length}
              color="#5c5c57"
              testIdPrefix="compat-sync-onlyrebrand"
              onItemClick={a => toggleToBase(a.sku)}
              onToggleAll={toggleAllToBase}
              allSelected={pendingToBase.size === selectableOnlyRebrand.length && selectableOnlyRebrand.length > 0}
              categorieEscluse={categorieEscluse}
              unlocked={unlocked}
            />
          </div>
        )}

        {/* Barra di conferma batch */}
        <div className="flex-none px-6 py-3 border-t border-[#e4e4e1] bg-[#fafafa] flex items-center justify-between">
          <div className="text-[13px] text-[#5c5c57]" data-testid="compat-sync-pending-summary">
            {totalPending === 0 ? (
              'Nessuna modifica selezionata'
            ) : (
              <>
                <strong className="text-[#1f1f1d]">{totalPending}</strong> modifiche selezionate
                {pendingToRebrand.size > 0 && ` · ${pendingToRebrand.size} → ${rebrandBrand}`}
                {pendingToBase.size > 0 && ` · ${pendingToBase.size} → ${baseBrand}`}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {totalPending > 0 && (
              <button
                onClick={() => { setPendingToRebrand(new Set()); setPendingToBase(new Set()); }}
                data-testid="compat-sync-clear-selection"
                className="text-[13px] text-[#5c5c57] hover:underline px-2"
              >
                Annulla selezione
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={totalPending === 0 || confirming}
              data-testid="compat-sync-confirm"
              className="px-4 py-2 rounded-[8px] bg-[#2b3a8f] text-white text-[13px] font-semibold hover:bg-[#243279] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Conferma sincronizzazione
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Colonna laterale ("Solo produttore" / "Solo rebrand") ──────────── */
function SideColumn({
  title, items, selectableCount, color, testIdPrefix, onItemClick, onToggleAll, allSelected, categorieEscluse, unlocked,
}: {
  title: string;
  items: MachineArticle[];
  selectableCount: number;
  color: string;
  testIdPrefix: string;
  onItemClick: (a: MachineArticle) => void;
  onToggleAll: () => void;
  allSelected: boolean;
  categorieEscluse: string[];
  unlocked: boolean;
}) {
  return (
    <div className="flex flex-col min-h-0">
      <div className="flex-none px-4 py-3 border-b border-[#f1f1ee]">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[13px] font-bold" style={{ color }}>
            {title} <span className="font-mono text-[#9b9b96]">({items.length})</span>
          </div>
          {selectableCount > 0 && (
            <button
              onClick={onToggleAll}
              data-testid={`${testIdPrefix}-select-all`}
              className="text-[11px] font-semibold text-[#2b3a8f] hover:underline flex-none"
            >
              {allSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="px-4 py-8 text-center text-[12px] text-[#9b9b96]" data-testid={`${testIdPrefix}-empty`}>Nessun articolo</div>
        ) : (
          items.map(a => {
            const locked = !unlocked && categorieEscluse.includes(a.cat);
            return (
              <div
                key={a.sku}
                data-testid={`${testIdPrefix}-row-${a.sku}`}
                onClick={locked ? undefined : () => onItemClick(a)}
                className={`flex items-center gap-2 px-4 py-2 border-b border-[#f7f7f5] transition-colors ${
                  locked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-[#fafafa]'
                }`}
                title={locked
                  ? 'Categoria esclusa dalla relazione di rebrand — sblocca il lucchetto per spostarla'
                  : 'Clicca per spostare in Condivisi'}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-mono font-semibold text-[#1f1f1d] truncate">{a.sku}</div>
                  <div className="text-[11px] text-[#5c5c57] truncate">{a.desc}</div>
                </div>
                {locked && <Lock className="w-3 h-3 flex-none text-[#9b9b96]" data-testid={`${testIdPrefix}-locked-${a.sku}`} />}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ── Colonna centrale ("Condivisi" + anteprima "in attesa") ─────────── */
function CenterColumn({
  shared, pendingItems, baseBrand, rebrandBrand, onUndoFromBase, onUndoFromRebrand,
}: {
  shared: MachineArticle[];
  pendingItems: PendingItem[];
  baseBrand: string;
  rebrandBrand: string;
  onUndoFromBase: (sku: string) => void;
  onUndoFromRebrand: (sku: string) => void;
}) {
  return (
    <div className="flex flex-col min-h-0">
      <div className="flex-none px-4 py-3 border-b border-[#f1f1ee]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[13px] font-bold" style={{ color: '#2b3a8f' }}>
            Condivisi <span className="font-mono text-[#9b9b96]">({shared.length})</span>
          </div>
          {pendingItems.length > 0 && (
            <span className="text-[11px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" data-testid="compat-sync-pending-badge">
              +{pendingItems.length} in attesa
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {shared.length === 0 && pendingItems.length === 0 ? (
          <div className="px-4 py-8 text-center text-[12px] text-[#9b9b96]" data-testid="compat-sync-shared-empty">Nessun articolo</div>
        ) : (
          <>
            {shared.map(a => (
              <div key={a.sku} data-testid={`compat-sync-shared-row-${a.sku}`} className="flex items-center gap-2 px-4 py-2 border-b border-[#f7f7f5]">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-mono font-semibold text-[#1f1f1d] truncate">{a.sku}</div>
                  <div className="text-[11px] text-[#5c5c57] truncate">{a.desc}</div>
                </div>
              </div>
            ))}
            {pendingItems.length > 0 && (
              <div className="px-4 py-1.5 bg-[#fafafa] border-y border-[#f1f1ee] text-[10px] font-bold tracking-wider text-amber-700 uppercase">
                In attesa di conferma
              </div>
            )}
            {pendingItems.map(p => (
              <div
                key={`${p.kind}-${p.article.sku}`}
                data-testid={`compat-sync-pending-row-${p.article.sku}`}
                onClick={() => p.kind === 'fromBase' ? onUndoFromBase(p.article.sku) : onUndoFromRebrand(p.article.sku)}
                className="flex items-center gap-2 px-4 py-2 border-b border-amber-100 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors"
                title="Clicca per annullare"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-mono font-semibold text-[#1f1f1d] truncate">{p.article.sku}</div>
                  <div className="text-[11px] text-[#5c5c57] truncate">{p.article.desc}</div>
                </div>
                <span className="flex-none text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded">
                  da {p.kind === 'fromBase' ? baseBrand : rebrandBrand}
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
